import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Query } from "@redux-requests/react";
import DropzoneArea from "components/DropZoneArea";
import get from "lodash/get";
import TablePagination from "@material-ui/core/TablePagination";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { fetchCountSuppliers } from "redux/actions/invoices";
import { isActionAllowed } from "components/HasPermission";
import roles from "redux/constants/roles";
import { CONFIG } from "utils/constants";
import Filter from "./Filter";
import useStyles from "../Invoices/List/style";
import Instructions from "./Component/ImportInstructions";
import {
	fetchSuppliers,
	createMultiSupplier,
	downloadImportSample,
	exportSuppliersExcel,
} from "../../redux/actions/suppliers";
import { INVOICE_TOTAL_AMOUNT, FETCH_COUNT_SUPPLIERS } from "../../redux/constants";
import TableCardItem from "../../components/TableCardItem";
import ConfirmationDialog from "./Component/ConfirmationDialog";
import { updateFilter, initFilter } from "../../redux/supplierFilterReducer";
import CircularLoader from "../../components/Loaders/CircularLoader";
import { currencyFormat, toast } from "../../utils";

export const List = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const [selected, setSelected] = React.useState([]);
	const [open, setOpen] = React.useState(false);
	const [scroll, setScroll] = React.useState("paper");
	const [files, setFiles] = React.useState([]);
	const filterSuppliers = useSelector(({ filterSuppliers: i }) => i);
	const [expanded, setExpanded] = React.useState(false);
	const [openDeletePopup, setOpenDeletePopup] = React.useState(false);
	const [selectedId, setSelectedId] = React.useState(0);
	const [exportLoading, setExportLoading] = React.useState({ export: false, example: false });

	const tenantCountry = useSelector(({ requests }) =>
		get(requests, "queries.FETCH_TENANT_COUNTRY.data")
	);
	const countryConfig = CONFIG[tenantCountry] || CONFIG.MA;
	const currentUser = useSelector(({ requests }) =>
		get(requests, "queries.FETCH_CURRENT_USER.data")
	);
	const currentUserPermissions = useMemo(() => get(currentUser, "permissions"), [currentUser]);
	const suppliers = useSelector(({ requests }) => get(requests, "queries.FETCH_SUPPLIERS.data"));

	const idSuppliers = "";
	const showActionBtns = currentUser && get(currentUser, "roles", []).includes(roles.manager);

	const onloadFiles = (f) => {
		setFiles(f);
	};
	const onDeleteFile = (file) => {
		files.splice(files.indexOf(file), 1);
		toast.info(t("fileRemovedMsg", { filename: file.name }), { autoClose: 3000 });
	};

	const acceptedInvoices = [".zip"];

	const handleSelectedAll = (checked) => {
		setSelected(checked ? idSuppliers : []);
	};

	const handleChangePage = (event, newPage) => {
		dispatch(updateFilter({ ...filterSuppliers, pageNo: newPage }));
	};

	const exportSuppliers = () => {
		setExportLoading({ ...exportLoading, export: true });
		dispatch(exportSuppliersExcel()).then(() =>
			setExportLoading({ ...exportLoading, export: false })
		);
	};

	const handleChangeRowsPerPage = (event) => {
		dispatch(
			updateFilter({
				...filterSuppliers,
				pageSize: +event.target.value,
				pageNo: 0,
			})
		);
	};

	const reloadSuppliers = () => {
		dispatch(fetchSuppliers(filterSuppliers));
		dispatch(fetchCountSuppliers());
	};

	useEffect(() => {
		reloadSuppliers();
	}, [filterSuppliers, dispatch]);

	useEffect(() => {
		dispatch(initFilter());
	}, [dispatch]);

	const onClickRow = (id) => {
		dispatch(initFilter());
		history.push(`/supplier/details/${id}`);
	};

	const onDeleteItem = (id) => {
		setOpenDeletePopup(true);
		setSelectedId(id);
	};

	const onclickUploadInvoices = () => {
		files.forEach((element) => {
			dispatch(createMultiSupplier(element)).then((res) => {
				const response = res.data;
				if (response.emptyLine) {
					toast.error(t("suppliers.empty.field.name.row.excel"));
					return;
				}
				if (response?.notConform) {
					toast.error(t("suppliers.error.sample"));
				} else if (response?.unsavedSuppliers?.length !== 0) {
					toast.error(
						`${t("suppliers.names.exist.already", {
							fournisseurs: response.unsavedSuppliers.join(", "),
						})}`
					);
				}
				reloadSuppliers();
			});
		});
	};

	const openInstructions = () => {
		setOpen(true);
		setScroll("paper");
	};

	const onClickDownloadSuppliersTemplate = () => {
		setExportLoading({ ...exportLoading, example: true });
		dispatch(downloadImportSample()).then((res) => {
			if (res.status === 200) openInstructions();
			setExportLoading({ ...exportLoading, example: false });
		});
	};

	const handleClose = () => {
		setOpen(false);
	};

	const descriptionElementRef = React.useRef(null);
	React.useEffect(() => {
		if (open) {
			const { current: descriptionElement } = descriptionElementRef;
			if (descriptionElement !== null) {
				descriptionElement.focus();
			}
		}
	}, [open]);

	const onDrop = () => {
		// toast.success(t("fileAddedMsg"), { autoClose: 3000 });
	};

	return (
		<>
			<Grid spacing={2} direction="column" xs={12} container className={classes.firstGrid}>
				{expanded && (
					<Grid container justify="flex-end">
						<IconButton aria-label="help" onClick={() => openInstructions()}>
							<HelpOutlineIcon />
						</IconButton>
					</Grid>
				)}
				<Grid
					spacing={2}
					container
					item
					xs={12}
					className={classes.headerZone}
					justify="center"
					alignItems="stretch"
				>
					<Grid item xs={6}>
						<DropzoneArea
							filesLimit={30}
							expanded={expanded}
							showFileNames
							useChipsForPreview
							dropzoneClass={classes.dropzone}
							dropzoneParagraphClass={classes.text}
							onChange={onloadFiles}
							onDelete={onDeleteFile}
							onDrop={onDrop}
							zoneMessage="suppliers.add.multiple"
							acceptedFiles={acceptedInvoices}
						/>
					</Grid>
					<Grid container item xs={2}>
						{expanded && (
							<>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									className={classes.expand}
									onClick={(e) => {
										onclickUploadInvoices(e);
										setExpanded(!expanded);
									}}
								>
									{t("upload.supplier")}
								</Button>
								<Button
									fullWidth
									variant="outlined"
									color="secondary"
									className={classes.expand}
									onClick={() => {
										onClickDownloadSuppliersTemplate();
									}}
									disabled={exportLoading.example}
								>
									{exportLoading.example && <CircularLoader size={12} />}&nbsp;
									{t("downloqd.template")}
								</Button>
							</>
						)}
						<Dialog
							open={open}
							onClose={handleClose}
							scroll={scroll}
							aria-labelledby="scroll-dialog-title"
							aria-describedby="scroll-dialog-description"
						>
							<DialogTitle id="scroll-dialog-title">
								{t("upload.instructions")}
								<IconButton
									aria-label="close"
									className={classes.closeButton}
									onClick={handleClose}
								>
									<CloseIcon />
								</IconButton>
							</DialogTitle>

							<DialogContent dividers={scroll === "paper"}>
								<DialogContentText
									id="scroll-dialog-description"
									ref={descriptionElementRef}
									tabIndex={-1}
								>
									<Instructions />
								</DialogContentText>
							</DialogContent>
						</Dialog>
					</Grid>
				</Grid>
				<Grid container direction="column-reverse">
					<Grid container justify="flex-end">
						<Grid container md={3} alignItems="flex-end">
							{!expanded &&
								showActionBtns &&
								isActionAllowed(currentUserPermissions, "Suppliers", "Add") && (
									<Button
										fullWidth
										variant="contained"
										color="secondary"
										className={`${classes.expand}`}
										onClick={() => {
											history.push("/addSupplier");
										}}
									>
										{t("supplier.add2.button")}
									</Button>
								)}
							{!expanded &&
								showActionBtns &&
								isActionAllowed(currentUserPermissions, "Suppliers", "Import") && (
									<Button
										fullWidth
										variant="contained"
										color="primary"
										className={`${classes.expand}`}
										onClick={() => {
											setExpanded(!expanded);
										}}
									>
										{t("supplier.add.button")}
									</Button>
								)}
							{!expanded && isActionAllowed(currentUserPermissions, "Suppliers", "Export") && (
								<Button
									fullWidth
									variant="outlined"
									color="primary"
									className={`${classes.expand} `}
									onClick={() => exportSuppliers()}
									disabled={exportLoading.export}
								>
									{exportLoading.export && <CircularLoader size={12} />}&nbsp;
									{t("supplier.export.suppliers.excel")}
								</Button>
							)}
						</Grid>
					</Grid>
					<Grid container item direction="column" xs={5}>
						<Grid item>
							<Typography variant="h6" color="secondary">
								<span style={{ whiteSpace: "nowrap" }}>{t("Total amount of invoices")} :</span>{" "}
								<span style={{ whiteSpace: "nowrap" }}>
									<Query type={INVOICE_TOTAL_AMOUNT}>
										{({ data }) => (data ? currencyFormat(data) : currencyFormat(0))}
									</Query>
									{` ${countryConfig.currency}`}
								</span>
							</Typography>
						</Grid>
						<Grid item>
							<Typography variant="subtitle2" color="textSecondary">
								{t("countSuppliers")} :
								<Query type={FETCH_COUNT_SUPPLIERS}>{({ data }) => ` ${data}`}</Query>
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid item container>
					<Filter
						selected={selected}
						setSelected={setSelected}
						handleSelectedAll={handleSelectedAll}
					/>
				</Grid>
				<Grid
					item
					// todo this is a temp fix
					style={{
						minHeight: "1200px",
						margin: "30px 0px",
						paddingBottom: "50px",
					}}
				>
					{get(suppliers, "content") ? (
						suppliers.content.map((row, i) => {
							const infoItems = [
								{
									multiLines: true,
									rows: [
										{ variant: "body1", value: row.email, align: "left" },
										{
											variant: "body2",
											color: "secondary",
											value: row.phone,
											align: "left",
										},
										{
											variant: "subtitle2",
											color: "textSecondary",
											value: row.address,
											align: "left",
										},
									],
								},
								{ label: t(tenantCountry === "FR" ? "VAT Number FR" : "VAT Number MA"), value: row.vatNumber },
								{ label: t("Company Siret"), value: row.companySiret },
							];
							return (
								<TableCardItem
									index={i}
									t={t}
									row={row}
									contactDetails
									infoItems={infoItems}
									onClickRow={onClickRow}
									onDeleteItem={onDeleteItem}
									refreshSuppliers={() => reloadSuppliers()}
									isSupplier
									withLogo
									showActionBtns={showActionBtns}
								/>
							);
						})
					) : (
						<CircularLoader />
					)}
					<TablePagination
						rowsPerPageOptions={[10, 25, 50]}
						component="div"
						count={get(suppliers, "totalElements")}
						rowsPerPage={filterSuppliers.pageSize}
						page={filterSuppliers.pageNo}
						onChangePage={handleChangePage}
						onChangeRowsPerPage={handleChangeRowsPerPage}
						labelRowsPerPage={t("pagination.rows.per.page")}
						labelDisplayedRows={({ from, to, count }) =>
							`${from}-${to} ${t("pagination.of")} ${count}`
						}
					/>
				</Grid>
			</Grid>
			<ConfirmationDialog
				open={openDeletePopup}
				setOpen={setOpenDeletePopup}
				id={selectedId}
				filterSuppliers={filterSuppliers}
			/>
		</>
	);
};

export default List;
